import { Grid, Typography } from '@mui/material';
import {
    ActionTypes,
    FilterModel,
    SortModel
} from '@tassat/common-definitions';
import { AdminUserForm } from '@tassat/forms';
import { UserValidationRecord } from '@tassat/fred-definitions';
import { AdminUserRecordMapped } from '@tassat/fred-extended-definitions';
import { Users as UsersIcon } from '@tassat/icon';
import { FormModal } from '@tassat/modal';
import { browserSessionStore } from '@tassat/utility';
import React from 'react';
import { Route } from 'react-router-dom';

import { submitAdminUserForm } from 'Containers/admin/forms/helpers/AdminUserFormFn';
import { validateUsername } from 'Containers/admin/forms/helpers/commonHelpers';
import Header from 'Containers/header';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAcgSelectOptions } from 'Selectors/acg';
import { getUserRoles, getUsers } from 'Selectors/common';
import { getAdminUsersWithRoleDisplay, getUserById } from 'Selectors/users';
import PageHeaderTitle from 'SharedComponents/pageHeader/pageHeaderTitle';

import AdminUserDetails from './adminUserDetails';
import AdminUsersGrid from './adminUsersGrid';
import { useFeatures } from '../../../../sharedcomponents/hooks/useFeatures';

import type { AdminUsersProps } from '.';

export const PAGE_TITLE = 'Admin Users';

export function AdminUsers(props: AdminUsersProps) {
    const { classes } = props;

    const userRoles = useAppSelector(getUserRoles);
    const adminUsers = useAppSelector(getAdminUsersWithRoleDisplay);
    const users = useAppSelector(getUsers);
    const currentUser = useAppSelector((state) => getUserById(state, browserSessionStore.getItem('userid') ?? '', false, '', ''));
    const [features] = useFeatures();
    const [ filterModel, setFilterModel ] = React.useState<{ string: FilterModel }>({} as { string: FilterModel });
    const [ sortModel, setSortModel ] = React.useState<SortModel[]>([]);
    const [ addUserModalOpen, setAddUserModalOpen ] = React.useState<boolean>(false);
    const [ formType, setFormType ] = React.useState<ActionTypes>(ActionTypes.add);
    const [ editUser, setEditUser ] = React.useState<AdminUserRecordMapped | UserValidationRecord>({} as AdminUserRecordMapped);
    const [ deleteAdminUser, setDeleteAdminUser ] = React.useState(null);
    const acgSelectOptions = useAppSelector((state) => getAcgSelectOptions(state, true, false));

    const editAdminUser = (adminUser, func) => {
        setDeleteAdminUser(func);
        setEditUser(adminUser);
        setFormType(ActionTypes.edit);
        setAddUserModalOpen(true);
    };

    return (
        <Grid
            className={classes.grow}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Header />
            <Grid
                className={classes.header}
                container
                direction='row'
                item
                xs={12}
            >
                <Grid
                    item
                    xs={5}
                >
                    <PageHeaderTitle
                        currentUser={currentUser}
                        form={true}
                        onClick={() => setAddUserModalOpen(!addUserModalOpen)}
                        title={PAGE_TITLE}
                    />
                </Grid>
            </Grid>
            <Grid
                container
                direction='row'
                item
                spacing={0}
                wrap='nowrap'
                xs={12}
            >
                <Grid
                    className={classes.gridContainer}
                    item
                    xs={5}
                >
                    <AdminUsersGrid
                        adminUsers={adminUsers}
                        filterModel={filterModel}
                        setFilterModel={setFilterModel}
                        setSortModel={setSortModel}
                        sortModel={sortModel}
                    />
                </Grid>
                <Grid
                    className={classes.userDetailsContainer}
                    item
                    xs={7}
                >
                    <Route
                        exact
                        path='/admin/users/:userId'
                        render={props => (
                            <AdminUserDetails
                                {...props}
                                editAdminUser={editAdminUser}
                                filterModel={filterModel}
                                isSSOEnabled={features.isSSOEnabled}
                                sortModel={sortModel}
                            />
                        )}
                    />
                    <Route
                        exact
                        path='/admin/users'
                        render={() => (
                            <Grid
                                alignItems='center'
                                className={classes.noSelectedUser}
                                container
                                direction='column'
                                item
                                spacing={0}
                                wrap='nowrap'
                            >
                                <UsersIcon className={classes.usersIcon} />
                                <Typography variant='body1'>Select a user on the left to view or edit information.</Typography>
                            </Grid>
                        )}
                    />
                </Grid>
            </Grid>
            <FormModal
                Form={AdminUserForm}
                formProps={{
                    isAdmin: true,
                    firmId: '-1',
                    actionType: formType,
                    editUser: editUser,
                    features: {
                        ...features,
                        loginType: features.authentication.Type
                    },
                    deleteAdminUser: deleteAdminUser,
                    users: users,
                    userRoles: userRoles,
                    currentUser: currentUser,
                    pcgSelectOptions: acgSelectOptions,
                    submitAdminUserForm: (values, opts) => submitAdminUserForm(values, opts),
                    validateUsernameAsync: validateUsername
                }}
                onClose={() => {
                    setAddUserModalOpen(false);
                    setFormType(ActionTypes.add);
                    setEditUser({} as AdminUserRecordMapped);
                }}
                open={addUserModalOpen}
                title={`${formType === ActionTypes.add ? 'New' : 'Edit'} Admin User`}
            />
        </Grid>
    );
}